/**
 * Modal Handler — Modal CPT + events; ClickDimensions mounts once per form root.
 */

import {
	bindModalFormIframeSizing,
	ensureModalFormsMounted,
	modalBodyFormsHealthy,
	mountClickDimensionsRoots,
} from './clickdimensions-loader.js';
import { initModalAuto } from './modal-auto.js';

/**
 * Multisite-safe REST base (subdirectory installs: /regatta/wp-json/… not /wp-json/…).
 */
function getRegattaRestBase() {
	if (window.regattaModal?.restBase) {
		const base = window.regattaModal.restBase;
		return base.endsWith('/') ? base : `${base}/`;
	}

	// WordPress prints the REST root in <head> (works on every subsite).
	const apiLink = document.querySelector('link[rel="https://api.w.org/"]');
	if (apiLink?.href) {
		const root = apiLink.href.endsWith('/') ? apiLink.href : `${apiLink.href}/`;
		return `${root}regatta/v1/`;
	}

	if (window.wpApiSettings?.root) {
		const root = window.wpApiSettings.root.endsWith('/')
			? window.wpApiSettings.root
			: `${window.wpApiSettings.root}/`;
		return `${root}regatta/v1/`;
	}

	// Fallback: first path segment is often the subsite slug (e.g. /regatta/).
	const { origin, pathname } = window.location;
	const siteMatch = pathname.match(/^(\/[^/]+\/)/);
	const siteBase = siteMatch ? siteMatch[1] : '/';
	return `${origin}${siteBase}wp-json/regatta/v1/`;
}

class ModalHandler {
	constructor() {
		this.cache = new Map();
		this.activeModal = null;
		this.scrollLockY = 0;
		/** @type {boolean} */
		this.lenisWasStopped = false;
		/** @type {Map<string, HTMLElement>} */
		this.preloadHosts = new Map();
		this.preloadPool = null;
		this.init();
	}

	/**
	 * Lock page scroll while keeping modal content scrollable (iOS-safe).
	 */
	lockBodyScroll() {
		if (document.body.classList.contains('modal-open')) {
			return;
		}

		this.scrollLockY = window.scrollY || window.pageYOffset || 0;
		document.body.classList.add('modal-open');
		document.body.style.top = `-${this.scrollLockY}px`;

		// Lenis captures wheel globally; pause it so modal columns can scroll natively.
		if (window.lenis && typeof window.lenis.stop === 'function') {
			window.lenis.stop();
			this.lenisWasStopped = true;
		}
	}

	/**
	 * Restore page scroll position after modal closes.
	 */
	unlockBodyScroll() {
		if (!document.body.classList.contains('modal-open')) {
			return;
		}

		document.body.classList.remove('modal-open');
		document.body.style.top = '';
		window.scrollTo(0, this.scrollLockY);
		this.scrollLockY = 0;

		if (this.lenisWasStopped && window.lenis && typeof window.lenis.start === 'function') {
			window.lenis.start();
			this.lenisWasStopped = false;
		}
	}

	init() {
		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', () => this.setup());
		} else {
			this.setup();
		}
	}

	async setup() {
		this.attachEventListeners();
		try {
			await mountClickDimensionsRoots(document);
			await this.preloadContent();
		} catch (err) {
			console.warn('Modal / ClickDimensions setup failed:', err);
		}
	}

	attachEventListeners() {
		document.addEventListener('click', (e) => {
			// Overlay shells carry IDs too — ignore any click inside an open modal.
			if (e.target.closest('.modal-overlay.is-open')) {
				return;
			}

			const eventTrigger = e.target.closest('[data-event-id]');
			if (eventTrigger) {
				e.preventDefault();
				const eventId = eventTrigger.getAttribute('data-event-id');
				if (eventId) {
					this.openEventModal(eventId);
				}
				return;
			}

			const trigger = e.target.closest('.modal-trigger, [data-modal-id]');
			if (trigger) {
				e.preventDefault();
				const modalId = trigger.getAttribute('data-modal-id');
				if (modalId) {
					this.openModal(modalId);
				}
			}
		});

		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && this.activeModal) {
				this.closeModal();
			}
		});

		document.addEventListener('facetwp-loaded', () => {
			this.preloadContent().catch((err) => {
				console.warn('Modal preload after FacetWP refresh failed:', err);
			});
		});
	}

	/**
	 * Hidden container for off-screen modal shells (forms mount before open).
	 *
	 * @returns {HTMLElement}
	 */
	getPreloadPool() {
		if (!this.preloadPool) {
			this.preloadPool = document.createElement('div');
			this.preloadPool.id = 'regatta-modal-preload';
			this.preloadPool.className = 'regatta-modal-preload';
			this.preloadPool.setAttribute('aria-hidden', 'true');
			document.body.appendChild(this.preloadPool);
		}

		return this.preloadPool;
	}

	/**
	 * @param {object} payload REST payload.
	 * @returns {boolean}
	 */
	payloadHasForm(payload) {
		if (!payload) {
			return false;
		}

		if (payload.has_clickdimensions) {
			return true;
		}

		if (payload.form_html) {
			return true;
		}

		return (
			typeof payload.body_html === 'string' && payload.body_html.includes('regatta-cd-form')
		);
	}

	/**
	 * Build modal DOM (optionally kept hidden for preload).
	 *
	 * @param {object} options Options.
	 * @returns {{ overlay: HTMLElement, body: HTMLElement, closeButton: HTMLButtonElement }}
	 */
	createOverlayElements({
		overlayClass,
		containerClass,
		bodyClass,
		overlayIdAttr,
		id,
		title,
		bodyHtml,
		preloadKey = '',
	}) {
		const overlay = document.createElement('div');
		overlay.className = overlayClass;
		if (overlayIdAttr) {
			overlay.setAttribute(overlayIdAttr, id);
		}
		overlay.setAttribute('role', 'dialog');
		overlay.setAttribute('aria-modal', 'true');
		overlay.setAttribute('aria-labelledby', `modal-title-${id}`);

		if (preloadKey) {
			overlay.dataset.preloadKey = preloadKey;
		}

		const modal = document.createElement('div');
		modal.className = containerClass;

		// const header = document.createElement('div');
		// header.className = 'modal-header';

		const titleEl = document.createElement('h2');
		titleEl.className = 'modal-title';
		titleEl.id = `modal-title-${id}`;
		titleEl.innerHTML = title;

		const closeButton = document.createElement('button');
		closeButton.className = 'modal-close';
		closeButton.setAttribute('aria-label', 'Close modal');
		closeButton.innerHTML = '&times;';
		closeButton.addEventListener('click', () => this.closeModal());

		// header.appendChild(titleEl);
		modal.appendChild(closeButton);

		const body = document.createElement('div');
		body.className = bodyClass;
		body.setAttribute('data-lenis-prevent', '');
		body.innerHTML = bodyHtml;

		// modal.appendChild(header);
		modal.appendChild(body);
		overlay.appendChild(modal);

		overlay.addEventListener('click', (e) => {
			if (e.target === overlay) {
				this.closeModal();
			}
		});

		return { overlay, body, closeButton };
	}

	/**
	 * Mount modal + ClickDimensions form off-screen for faster open.
	 *
	 * @param {string} cacheKey Cache key (modal-123 / event-456).
	 * @param {object} data REST payload.
	 * @param {object} meta overlayIdAttr + id.
	 * @returns {Promise<void>}
	 */
	async preloadModalShell(cacheKey, data, { overlayIdAttr, id }) {
		if (this.preloadHosts.has(cacheKey) || !this.payloadHasForm(data)) {
			return;
		}

		const layout = this.getLayoutClasses(data);
		const { overlay, body } = this.createOverlayElements({
			overlayClass: layout.overlay,
			containerClass: layout.container,
			bodyClass: layout.body,
			overlayIdAttr,
			id,
			title: data.title,
			bodyHtml: this.buildBodyHtml(data),
			preloadKey: cacheKey,
		});

		overlay.classList.add('regatta-modal-preload-host');
		overlay.hidden = true;
		this.getPreloadPool().appendChild(overlay);

		try {
			await ensureModalFormsMounted(body);
			if (!modalBodyFormsHealthy(body)) {
				overlay.remove();
				return;
			}
			this.preloadHosts.set(cacheKey, overlay);
		} catch (err) {
			console.warn('ClickDimensions preload in modal failed:', cacheKey, err);
			overlay.remove();
		}
	}

	async preloadContent() {
		const keys = new Set();

		document.querySelectorAll('[data-modal-id]').forEach((el) => {
			const id = el.getAttribute('data-modal-id');
			if (id) keys.add(`modal-${id}`);
		});

		document.querySelectorAll('[data-event-id]').forEach((el) => {
			const id = el.getAttribute('data-event-id');
			if (id) keys.add(`event-${id}`);
		});

		for (const key of keys) {
			if (key.startsWith('modal-')) {
				const id = key.replace('modal-', '');
				const data = await this.fetchModal(id, { silent: true });
				if (data) {
					await this.preloadModalShell(key, data, {
						overlayIdAttr: 'data-regatta-modal-id',
						id,
					});
				}
			} else {
				const id = key.replace('event-', '');
				const data = await this.fetchEvent(id, { silent: true });
				if (data) {
					await this.preloadModalShell(key, data, {
						overlayIdAttr: 'data-regatta-event-id',
						id,
					});
				}
			}
		}
	}

	buildBodyHtml(payload) {
		if (payload.body_html) {
			return payload.body_html;
		}

		let bodyHtml = '';

		if (payload.event_date_label) {
			bodyHtml += `<p class="modal-body__event-date">${payload.event_date_label}</p>`;
		}
		if (payload.event_location) {
			bodyHtml += `<p class="modal-body__event-location">${payload.event_location}</p>`;
		}
		if (payload.content) {
			bodyHtml += `<div class="modal-body__content">${payload.content}</div>`;
		}
		if (payload.form_html) {
			bodyHtml += `<div class="modal-body__form">${payload.form_html}</div>`;
		}

		return bodyHtml;
	}

	getLayoutClasses(payload) {
		const defaults = {
			overlay: 'modal-overlay',
			container: 'modal-container',
			body: 'modal-body',
		};

		if (!payload?.layout_classes) {
			if (payload?.source === 'event') {
				defaults.overlay += ' modal-overlay--event';
				defaults.container += ' modal-container--event';
				defaults.body += ' modal-body--event';
			}
			return defaults;
		}

		return { ...defaults, ...payload.layout_classes };
	}

	async fetchModal(modalId, { silent = false } = {}) {
		const cacheKey = `modal-${modalId}`;
		if (this.cache.has(cacheKey)) {
			return this.cache.get(cacheKey);
		}

		const url = `${getRegattaRestBase()}modal/${modalId}`;

		try {
			const response = await fetch(url);
			const data = await response.json().catch(() => ({}));

			if (!response.ok) {
				const message = data?.message || `Modal ${modalId} could not be loaded`;
				if (!silent) {
					console.warn(message, { code: data?.code, status: response.status, url });
				}
				return null;
			}

			this.cache.set(cacheKey, data);
			return data;
		} catch (error) {
			if (!silent) {
				console.error('Error fetching modal:', error, url);
			}
			return null;
		}
	}

	async fetchEvent(eventId, { silent = false } = {}) {
		const cacheKey = `event-${eventId}`;
		if (this.cache.has(cacheKey)) {
			return this.cache.get(cacheKey);
		}

		const url = `${getRegattaRestBase()}event/${eventId}`;

		try {
			const response = await fetch(url);
			const data = await response.json().catch(() => ({}));

			if (!response.ok) {
				if (!silent) {
					console.warn(data?.message || `Event ${eventId} could not be loaded`, {
						code: data?.code,
						status: response.status,
						url,
					});
				}
				return null;
			}

			this.cache.set(cacheKey, data);
			return data;
		} catch (error) {
			if (!silent) {
				console.error('Error fetching event:', error, url);
			}
			return null;
		}
	}

	async openModal(modalId) {
		const cacheKey = `modal-${modalId}`;
		const data = await this.fetchModal(modalId);
		if (!data) {
			this.showErrorModal(
				'Modal niet beschikbaar',
				'De gekoppelde modal kon niet worden geladen. Controleer in het CMS of de modal bestaat en gepubliceerd is.'
			);
			return;
		}

		if (this.openPreloadedOverlay(cacheKey, { modalId, data })) {
			return;
		}

		const layout = this.getLayoutClasses(data);

		this.renderOverlay({
			overlayClass: layout.overlay,
			containerClass: layout.container,
			bodyClass: layout.body,
			overlayIdAttr: 'data-regatta-modal-id',
			id: modalId,
			title: data.title,
			bodyHtml: this.buildBodyHtml(data),
			detail: { modalId, data },
			mountForms: this.payloadHasForm(data),
		});
	}

	showErrorModal(title, message) {
		this.renderOverlay({
			overlayClass: 'modal-overlay modal-overlay--error',
			overlayIdAttr: 'data-regatta-modal-error',
			id: 'error',
			title,
			bodyHtml: `<p class="modal-body__error">${message}</p>`,
			detail: { error: true },
		});
	}

	async openEventModal(eventId) {
		const cacheKey = `event-${eventId}`;
		const data = await this.fetchEvent(eventId);
		if (!data) {
			this.showErrorModal(
				'Evenement niet beschikbaar',
				'Dit evenement kon niet worden geladen. Het is mogelijk niet meer zichtbaar op de site.'
			);
			return;
		}

		if (this.openPreloadedOverlay(cacheKey, { eventId, data })) {
			return;
		}

		const layout = this.getLayoutClasses(data);

		this.renderOverlay({
			overlayClass: layout.overlay,
			containerClass: layout.container,
			bodyClass: layout.body,
			overlayIdAttr: 'data-regatta-event-id',
			id: eventId,
			title: data.title,
			bodyHtml: this.buildBodyHtml(data),
			detail: { eventId, data },
			mountForms: this.payloadHasForm(data),
		});
	}

	/**
	 * Use an off-screen preloaded shell when forms are still healthy.
	 *
	 * @param {string} cacheKey modal-* / event-*.
	 * @param {object} detail Event detail for modal:opened.
	 * @returns {boolean} True when a preloaded overlay was shown.
	 */
	openPreloadedOverlay(cacheKey, detail) {
		const preloaded = this.preloadHosts.get(cacheKey);
		if (!preloaded) {
			return false;
		}

		const body = preloaded.querySelector('[class*="modal-body"]');
		if (body && this.payloadHasForm(detail.data) && !modalBodyFormsHealthy(body)) {
			preloaded.remove();
			this.preloadHosts.delete(cacheKey);
			return false;
		}

		this.preloadHosts.delete(cacheKey);
		this.showOverlay(preloaded, {
			detail,
			mountForms: this.payloadHasForm(detail.data),
		});
		return true;
	}

	/**
	 * Scrollable region in a split modal (theme column or editor block column).
	 *
	 * @param {HTMLElement} overlay Modal overlay.
	 * @returns {HTMLElement|null}
	 */
	findModalScrollMain(overlay) {
		return (
			overlay.querySelector('.modal-column--main') ||
			overlay.querySelector('.modal-event--split .modal-event__main') ||
			null
		);
	}

	/**
	 * Split layout: ensure wheel scroll reaches the main column (image / side column stays fixed).
	 *
	 * @param {HTMLElement} overlay Modal overlay.
	 */
	bindSplitModalScroll(overlay) {
		if (overlay.dataset.splitScrollBound) {
			return;
		}

		const container = overlay.querySelector('.modal-container--split');
		const main = this.findModalScrollMain(overlay);

		if (!container || !main) {
			return;
		}

		overlay.dataset.splitScrollBound = '1';
		main.setAttribute('data-lenis-prevent', '');

		const scrollMain = (e) => {
			if (e.target.closest('iframe')) {
				return;
			}

			// Split event modals: form column fills height; scroll inside the iframe.
			if (main.querySelector('.modal-event__form')) {
				return;
			}

			if (main.scrollHeight <= main.clientHeight) {
				return;
			}

			const { scrollTop, clientHeight, scrollHeight } = main;
			const maxScroll = scrollHeight - clientHeight;
			const scrollingDown = e.deltaY > 0;
			const scrollingUp = e.deltaY < 0;

			// Native scroll inside main when there is room to move.
			if (main.contains(e.target)) {
				if (scrollingDown && scrollTop < maxScroll - 1) {
					return;
				}
				if (scrollingUp && scrollTop > 0) {
					return;
				}
			}

			const next = Math.max(0, Math.min(maxScroll, scrollTop + e.deltaY));
			if (next === scrollTop) {
				return;
			}

			e.preventDefault();
			main.scrollTop = next;
		};

		container.addEventListener('wheel', scrollMain, { passive: false, capture: true });
	}

	/**
	 * Show a prebuilt or fresh overlay.
	 *
	 * @param {HTMLElement} overlay Overlay element.
	 * @param {object} options detail, mountForms.
	 */
	showOverlay(overlay, { detail, mountForms = false }) {
		if (this.activeModal) {
			this.closeModal();
		}

		const body = overlay.querySelector('[class*="modal-body"]');
		const closeButton = overlay.querySelector('.modal-close');

		const needsForm = Boolean(body && detail?.data && this.payloadHasForm(detail.data));

		overlay.classList.remove('regatta-modal-preload-host');
		overlay.hidden = false;
		overlay.removeAttribute('aria-hidden');
		overlay.setAttribute('data-lenis-prevent', '');

		document.body.appendChild(overlay);
		this.activeModal = overlay;
		this.lockBodyScroll();

		if (closeButton) {
			setTimeout(() => closeButton.focus(), 100);
		}

		this.bindSplitModalScroll(overlay);

		requestAnimationFrame(() => {
			overlay.classList.add('is-open');

			const openPromise =
				needsForm && body
					? ensureModalFormsMounted(body).catch((err) => {
							console.warn('ClickDimensions mount in modal failed:', err);
						})
					: Promise.resolve();

			openPromise.then(() => {
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						bindModalFormIframeSizing(overlay);
						document.dispatchEvent(new CustomEvent('modal:opened', { detail }));
					});
				});
			});
		});
	}

	renderOverlay({
		overlayClass,
		containerClass = 'modal-container',
		bodyClass = 'modal-body',
		overlayIdAttr,
		id,
		title,
		bodyHtml,
		detail,
		mountForms = false,
	}) {
		const { overlay } = this.createOverlayElements({
			overlayClass,
			containerClass,
			bodyClass,
			overlayIdAttr,
			id,
			title,
			bodyHtml,
		});

		this.showOverlay(overlay, { detail, mountForms });
	}

	closeModal() {
		if (!this.activeModal) return;

		const overlay = this.activeModal;
		const modalId =
			overlay.getAttribute('data-regatta-modal-id') ||
			overlay.getAttribute('data-regatta-event-id');
		const preloadKey = overlay.dataset.preloadKey || '';

		overlay.classList.remove('is-open');
		overlay.classList.add('is-closing');

		setTimeout(() => {
			// Do not recycle preloaded shells — hidden iframes often stop working after DOM moves / time on page.
			if (preloadKey) {
				this.preloadHosts.delete(preloadKey);
			}
			if (overlay.parentNode) {
				overlay.parentNode.removeChild(overlay);
			}

			this.activeModal = null;
			this.unlockBodyScroll();

			document.dispatchEvent(
				new CustomEvent('modal:closed', { detail: { modalId } })
			);
		}, 300);
	}

	closeAllModals() {
		this.closeModal();
	}
}

const modalHandler = new ModalHandler();
initModalAuto(modalHandler);

export default modalHandler;
